/**
 * AdminPanel.jsx — created Feb 28 2026 per user request: "Let's reshape
 * the app, first for admin. Landing page would consist of the contents
 * of the screenshots organized into buttons".
 *
 * Renders:
 *   1. Hero card (avatar · name · ADMIN badge · live snapshot stats)
 *   2. Vertical pill button list — every admin tool surfaced from the
 *      existing /admin tabs PLUS Admin Profile / Broadcast / Reader View.
 *
 * Each pill either deep-links into /admin with `state.tab`, opens a
 * modal (Broadcast), or navigates to a dedicated full page.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield,
  User as UserIcon,
  Megaphone,
  BookOpen,
  Database,
  Sparkles,
  DollarSign,
  Store,
  ImageOff,
  BarChart3,
  Users as UsersIcon,
  Lock,
  Trophy,
  FlaskConical,
  Wallet,
  Coins,
  MessageSquare,
  FileText,
  FolderCog,
  ChevronRight,
  Flame,
  Pin,
  Crown,
  Link2,
  Pencil,
  Zap,
  ShieldOff,
  UserCog,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { checkAuth, authFetch } from '../utils/authUtils';
import AdminAnnouncementComposer from '../components/AdminAnnouncementComposer';
import ConfirmDeleteDialog from '../components/ConfirmDeleteDialog';
import toast from 'react-hot-toast';
import { setAdminViewingAs } from '../utils/spaceContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Static class map — Tailwind's JIT only picks up literal class strings,
// so dynamic `bg-${accent}-500/15` will get purged in prod for colors that
// don't already appear elsewhere in the codebase. Safelist via this map.
const ACCENT_CLS = {
  indigo:  { bg: 'bg-indigo-500/15',  border: 'border-indigo-400/30',  text: 'text-indigo-300'  },
  fuchsia: { bg: 'bg-fuchsia-500/15', border: 'border-fuchsia-400/30', text: 'text-fuchsia-300' },
  emerald: { bg: 'bg-emerald-500/15', border: 'border-emerald-400/30', text: 'text-emerald-300' },
  cyan:    { bg: 'bg-cyan-500/15',    border: 'border-cyan-400/30',    text: 'text-cyan-300'    },
  amber:   { bg: 'bg-amber-500/15',   border: 'border-amber-400/30',   text: 'text-amber-300'   },
  rose:    { bg: 'bg-rose-500/15',    border: 'border-rose-400/30',    text: 'text-rose-300'    },
  slate:   { bg: 'bg-slate-500/15',   border: 'border-slate-400/30',   text: 'text-slate-300'   },
  pink:    { bg: 'bg-pink-500/15',    border: 'border-pink-400/30',    text: 'text-pink-300'    },
  purple:  { bg: 'bg-purple-500/15',  border: 'border-purple-400/30',  text: 'text-purple-300'  },
};

const AdminPanel = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [snapshot, setSnapshot] = useState(null); // {total, readers, writers, admins, new_30d}
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  // God Mode confirm + spinner state (High King only)
  const [godConfirmOpen, setGodConfirmOpen] = useState(false);
  const [godBusy, setGodBusy] = useState(false);
  // God Mode action selector — what the confirm dialog should fire.
  // Shape: { action: 'equip'|'strip', scope: 'all'|'single', email?: string }
  const [godAction, setGodAction] = useState(null);
  // Per-user targeting modal — High King opens this to type an email.
  const [godUserModalOpen, setGodUserModalOpen] = useState(false);
  const [godUserModalEmail, setGodUserModalEmail] = useState('');
  const [godUserModalAction, setGodUserModalAction] = useState('equip');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await checkAuth();
        if (cancelled) return;
        if (!u || (u.role !== 'admin' && u.role !== 'coadmin' && u.role !== 'king')) {
          navigate('/admin-login');
          return;
        }
        // Ensure we're back in 'admin' viewing mode when an admin lands here.
        setAdminViewingAs('admin');
        setUser(u);
        try {
          const r = await authFetch(`${BACKEND_URL}/api/admin/users`);
          if (r.ok) {
            const d = await r.json();
            const users = d.users || [];
            const cutoff30 = Date.now() - 30 * 24 * 60 * 60 * 1000;
            const cutoff7 = Date.now() - 7 * 24 * 60 * 60 * 1000;
            const tsOf = (x) => x.created_at ? new Date(x.created_at).getTime() : 0;
            const new30 = users.filter((x) => tsOf(x) >= cutoff30).length;
            const new7Total   = users.filter((x) => tsOf(x) >= cutoff7).length;
            const new7Readers = users.filter((x) => tsOf(x) >= cutoff7 && (x.role || 'user') === 'user').length;
            const new7Writers = users.filter((x) => tsOf(x) >= cutoff7 && x.role === 'writer').length;
            setSnapshot({
              total: users.length,
              readers: users.filter((x) => (x.role || 'user') === 'user').length,
              writers: users.filter((x) => x.role === 'writer').length,
              admins: users.filter((x) => x.role === 'admin').length,
              new_30d: new30,
              delta_7d_total: new7Total,
              delta_7d_readers: new7Readers,
              delta_7d_writers: new7Writers,
            });
          }
        } catch { /* non-fatal */ }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  // High King only — strict admin role. Kings/coadmins see the rest of
  // the panel but never see the God Mode button.
  const isHighKing = user?.role === 'admin';

  // Unified God Mode runner. `action` ∈ ('equip','strip'),
  // `scope` ∈ ('all','single'); when 'single', `email` must be set.
  const runGodMode = async (action, scope, email) => {
    setGodConfirmOpen(false);
    setGodAction(null);
    setGodBusy(true);
    const verb = action === 'equip' ? 'Equipping' : 'Stripping';
    const tid = toast.loading(`${verb} ${scope === 'single' ? email : 'the realm'}…`);
    try {
      const body = scope === 'single' ? { email: (email || '').trim().toLowerCase() } : {};
      const r = await authFetch(`${BACKEND_URL}/api/admin/godmode/${action}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(d.detail || `Failed (${r.status})`, { id: tid });
        return;
      }
      if (action === 'equip') {
        toast.success(
          `God Mode equipped on ${d.equipped} account${d.equipped === 1 ? '' : 's'} — ` +
          `+${(d.perks?.coins || 0).toLocaleString()} coins · L${d.perks?.level} · ` +
          `${d.perks?.achievements_unlocked} achievements unlocked.`,
          { id: tid, duration: 6000 },
        );
      } else {
        toast.success(
          `God Mode stripped from ${d.stripped} account${d.stripped === 1 ? '' : 's'}.`,
          { id: tid, duration: 5000 },
        );
      }
    } catch (e) {
      toast.error(e?.message || 'Network error', { id: tid });
    } finally {
      setGodBusy(false);
    }
  };

  // Confirm-dialog descriptor builder — keeps the dialog reusable for all
  // 4 actions (equip-all, strip-all, equip-single, strip-single).
  const godDialog = (() => {
    if (!godAction) return null;
    const { action, scope, email } = godAction;
    if (action === 'equip' && scope === 'all') {
      return {
        title: 'Equip God Mode for the realm?',
        description:
          'Every crown account (admins, coadmins, kings) — including yourself — will be granted: 1,000,000 coins, Level 100 (reader + scribe), every achievement, a 365-day streak, Top Tier subscription, and vault access. A snapshot of each prior state is captured so Strip can restore it later.',
        confirmLabel: 'Equip the realm',
      };
    }
    if (action === 'strip' && scope === 'all') {
      return {
        title: 'Strip God Mode from the realm?',
        description:
          'Every crown account will be reverted. When a pre-equip snapshot exists, the exact prior stats are restored; otherwise the account falls back to a safe baseline (free tier, 0 coins, Level 1, no streak, no achievements).',
        confirmLabel: 'Strip the realm',
      };
    }
    if (action === 'equip' && scope === 'single') {
      return {
        title: `Equip God Mode on ${email}?`,
        description:
          `${email} will be granted the full perk bundle (1M coins, Level 100, all achievements, 365-day streak, Top Tier, vault access). A snapshot of their prior state is captured for Strip.`,
        confirmLabel: `Equip ${email}`,
      };
    }
    return {
      title: `Strip God Mode from ${email}?`,
      description:
        `${email} will be reverted to their pre-equip snapshot when present, otherwise to the safe baseline (free tier, 0 coins, Level 1, no streak, no achievements).`,
      confirmLabel: `Strip ${email}`,
    };
  })();

  // Button manifest — order chosen so the most frequent actions sit up top.
  const buttons = [
    // Feb 13 2026 — God Mode. Strict High King only (`role === 'admin'`).
    // Equip-all/Strip-all bulk against every crown; "God Mode → User…"
    // opens a modal where the High King types an email to target a
    // single account. Each fires through the 5-sec countdown confirm
    // dialog before any state mutation.
    ...(isHighKing ? [
      {
        key: 'godmode-equip-all',
        label: godBusy ? 'Working…' : 'Equip God Mode (all crowns)',
        icon: Zap,
        accent: 'amber',
        onClick: () => !godBusy && (setGodAction({ action: 'equip', scope: 'all' }), setGodConfirmOpen(true)),
        testid: 'admin-panel-btn-godmode',
      },
      {
        key: 'godmode-strip-all',
        label: 'Strip God Mode (all crowns)',
        icon: ShieldOff,
        accent: 'rose',
        onClick: () => !godBusy && (setGodAction({ action: 'strip', scope: 'all' }), setGodConfirmOpen(true)),
        testid: 'admin-panel-btn-godmode-strip',
      },
      {
        key: 'godmode-user',
        label: 'God Mode → User…',
        icon: UserCog,
        accent: 'amber',
        onClick: () => !godBusy && (setGodUserModalEmail(''), setGodUserModalAction('equip'), setGodUserModalOpen(true)),
        testid: 'admin-panel-btn-godmode-user',
      },
    ] : []),
    { key: 'profile',     label: 'High King Profile',       icon: UserIcon,      accent: 'indigo',  onClick: () => navigate('/profile'),                                  testid: 'admin-panel-btn-profile' },
    { key: 'broadcast',   label: 'Broadcast',           icon: Megaphone,     accent: 'fuchsia', onClick: () => setBroadcastOpen(true),                                testid: 'admin-panel-btn-broadcast' },
    { key: 'reader-view', label: 'Reader View',         icon: BookOpen,      accent: 'indigo',  onClick: () => { setAdminViewingAs('reader'); navigate('/dashboard'); }, testid: 'admin-panel-btn-reader-view' },
    // Central Bank — admin treasury (Coins, deposit/disburse, ledger).
    // Feb 9 2026: user couldn't find it from this menu, so it now has its
    // own pill that deep-links into the /admin Central Bank tab.
    { key: 'central-bank', label: 'Central Bank',       icon: Coins,         accent: 'amber',   onClick: () => navigate('/admin', { state: { tab: 'central-bank' } }), testid: 'admin-panel-btn-central-bank' },
    // Feb 12 2026 — surface the King/Ambassador hierarchy manager. Deep-links
    // into the same tab where AmbassadorSlotsPanel renders.
    { key: 'kings',        label: 'Appoint King',       icon: Crown,         accent: 'amber',   onClick: () => navigate('/admin', { state: { tab: 'central-bank' } }), testid: 'admin-panel-btn-appoint-king' },
    { key: 'merge',        label: 'Link Accounts',      icon: Link2,         accent: 'fuchsia', onClick: () => navigate('/admin', { state: { tab: 'central-bank' } }), testid: 'admin-panel-btn-merge-accounts' },
    { key: 'activity',     label: 'User Activity',      icon: BarChart3,     accent: 'cyan',    onClick: () => navigate('/admin/activity'),                            testid: 'admin-panel-btn-activity' },
    { key: 'quest-review', label: 'Quest Review',       icon: Trophy,        accent: 'amber',   onClick: () => navigate('/admin/quest-review'),                       testid: 'admin-panel-btn-quest-review' },
    { key: 'edit-grants',  label: 'Edit Requests',      icon: Pencil,        accent: 'rose',    onClick: () => navigate('/admin/edit-grants'),                        testid: 'admin-panel-btn-edit-grants' },
    { key: 'users',       label: 'User Management',     icon: UsersIcon,     accent: 'emerald', onClick: () => navigate('/admin', { state: { tab: 'users' } }),       testid: 'admin-panel-btn-users' },
    { key: 'contracts',   label: 'Writer Contracts',    icon: FileText,      accent: 'amber',   onClick: () => navigate('/admin', { state: { tab: 'contracts' } }),   testid: 'admin-panel-btn-contracts' },
    { key: 'analytics',   label: 'Analytics',           icon: BarChart3,     accent: 'cyan',    onClick: () => navigate('/admin', { state: { tab: 'analytics' } }),   testid: 'admin-panel-btn-analytics' },
    { key: 'snapshot',    label: 'Database Snapshot',   icon: Database,      accent: 'emerald', onClick: () => navigate('/admin', { state: { tab: 'users' } }),       testid: 'admin-panel-btn-snapshot' },
    { key: 'featured',    label: 'Featured Rail',       icon: Sparkles,      accent: 'amber',   onClick: () => navigate('/admin'),                                    testid: 'admin-panel-btn-featured' },
    { key: 'payments',    label: 'Payments',            icon: DollarSign,    accent: 'emerald', onClick: () => navigate('/admin', { state: { tab: 'payments' } }),    testid: 'admin-panel-btn-payments' },
    { key: 'payouts',     label: 'Payouts',             icon: Wallet,        accent: 'emerald', onClick: () => navigate('/admin', { state: { tab: 'payments' } }),    testid: 'admin-panel-btn-payouts' },
    { key: 'shop',        label: 'Shop',                icon: Store,         accent: 'amber',   onClick: () => navigate('/admin', { state: { tab: 'shop' } }),        testid: 'admin-panel-btn-shop' },
    { key: 'audit',       label: 'Audit',               icon: ImageOff,      accent: 'rose',    onClick: () => navigate('/admin', { state: { tab: 'audit' } }),       testid: 'admin-panel-btn-audit' },
    { key: 'content',     label: 'Content Library',     icon: FileText,      accent: 'indigo',  onClick: () => navigate('/admin', { state: { tab: 'content' } }),     testid: 'admin-panel-btn-content' },
    { key: 'pending',     label: 'Pending Approvals',   icon: FileText,      accent: 'amber',   onClick: () => navigate('/admin', { state: { tab: 'pending' } }),     testid: 'admin-panel-btn-pending' },
    { key: 'manage',      label: 'Manage Content',      icon: FolderCog,     accent: 'slate',   onClick: () => navigate('/manage-content'),                           testid: 'admin-panel-btn-manage' },
    { key: 'vault',       label: 'Vault Manager',       icon: Lock,          accent: 'fuchsia', onClick: () => navigate('/admin', { state: { tab: 'vault' } }),       testid: 'admin-panel-btn-vault' },
    { key: 'quests',      label: 'Quests',              icon: Trophy,        accent: 'amber',   onClick: () => navigate('/admin', { state: { tab: 'quests' } }),      testid: 'admin-panel-btn-quests' },
    { key: 'top-series',  label: 'Top Series Pins',     icon: Pin,           accent: 'amber',   onClick: () => navigate('/admin', { state: { tab: 'top-series' } }),  testid: 'admin-panel-btn-top-series' },
    { key: 'experiments', label: 'Experiments',         icon: FlaskConical,  accent: 'fuchsia', onClick: () => navigate('/admin', { state: { tab: 'experiments' } }), testid: 'admin-panel-btn-experiments' },
    { key: 'messages',    label: 'Messages',            icon: MessageSquare, accent: 'pink',    onClick: () => navigate('/admin', { state: { tab: 'messages' } }),    testid: 'admin-panel-btn-messages' },
    { key: 'writers',     label: 'Scribe Applications', icon: UsersIcon,     accent: 'purple',  onClick: () => navigate('/admin', { state: { tab: 'writers' } }),     testid: 'admin-panel-btn-writers' },
  ];

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <p className="text-gray-400">Loading admin panel…</p>
      </div>
    );
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-6" data-testid="admin-panel-page">
      {/* Hero card */}
      <section
        className="rounded-2xl bg-gradient-to-br from-red-900/40 via-slate-800/60 to-slate-900/80 border border-red-500/30 p-5 sm:p-6 mb-6 shadow-lg shadow-red-900/20"
        data-testid="admin-panel-hero"
      >
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-red-500 to-pink-600 flex items-center justify-center flex-shrink-0 shadow-lg shadow-red-900/40">
            <Shield className="w-8 h-8 sm:w-10 sm:h-10 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <h1 className="text-xl sm:text-2xl font-bold text-white truncate">{user?.name || 'High King'}</h1>
              <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center gap-1 tracking-widest">
                <Shield className="w-3 h-3" /> HIGH KING
              </span>
            </div>
            <p className="text-[11px] sm:text-xs text-red-300/90 uppercase tracking-widest font-bold">
              High King — Realm Steward
            </p>
            {/* Live snapshot stats — each pill deep-links to the matching
                view inside /admin (state.tab + state.roleFilter so the
                Users tab opens already-filtered). The 30D chip jumps to
                the Analytics tab where the onboarding funnel lives. */}
            {snapshot && (
              <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="admin-panel-stats">
                <button
                  type="button"
                  onClick={() => navigate('/admin', { state: { tab: 'users', roleFilter: 'all' } })}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-slate-900/50 border border-slate-700 hover:bg-slate-800 hover:border-slate-500 transition-colors cursor-pointer"
                  data-testid="admin-panel-stat-all"
                  aria-label={`View all ${snapshot.total} users — ${snapshot.delta_7d_total} new this week`}
                >
                  <UsersIcon className="w-3.5 h-3.5 text-indigo-300" />
                  <span className="text-xs font-bold text-white tabular-nums">{snapshot.total}</span>
                  <span className="text-[10px] uppercase text-slate-400">All</span>
                  {snapshot.delta_7d_total > 0 && (
                    <span
                      className="ml-0.5 inline-flex items-center px-1 py-px rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold tabular-nums"
                      data-testid="admin-panel-stat-all-delta"
                      title={`${snapshot.delta_7d_total} new in the last 7 days`}
                    >
                      +{snapshot.delta_7d_total}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin', { state: { tab: 'users', roleFilter: 'user' } })}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-500/15 border border-blue-400/30 hover:bg-blue-500/25 hover:border-blue-300/50 transition-colors cursor-pointer"
                  data-testid="admin-panel-stat-readers"
                  aria-label={`View ${snapshot.readers} readers — ${snapshot.delta_7d_readers} new this week`}
                >
                  <span className="text-xs font-bold text-blue-200 tabular-nums">{snapshot.readers}</span>
                  <span className="text-[10px] uppercase text-blue-300">Readers</span>
                  {snapshot.delta_7d_readers > 0 && (
                    <span
                      className="ml-0.5 inline-flex items-center px-1 py-px rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold tabular-nums"
                      data-testid="admin-panel-stat-readers-delta"
                      title={`${snapshot.delta_7d_readers} new readers in the last 7 days`}
                    >
                      +{snapshot.delta_7d_readers}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin', { state: { tab: 'users', roleFilter: 'writer' } })}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-500/15 border border-purple-400/30 hover:bg-purple-500/25 hover:border-purple-300/50 transition-colors cursor-pointer"
                  data-testid="admin-panel-stat-writers"
                  aria-label={`View ${snapshot.writers} scribes — ${snapshot.delta_7d_writers} new this week`}
                >
                  <span className="text-xs font-bold text-purple-200 tabular-nums">{snapshot.writers}</span>
                  <span className="text-[10px] uppercase text-purple-300">Scribes</span>
                  {snapshot.delta_7d_scribes > 0 && (
                    <span
                      className="ml-0.5 inline-flex items-center px-1 py-px rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] font-bold tabular-nums"
                      data-testid="admin-panel-stat-writers-delta"
                      title={`${snapshot.delta_7d_writers} new scribes in the last 7 days`}
                    >
                      +{snapshot.delta_7d_writers}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/admin', { state: { tab: 'analytics' } })}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-400/30 hover:bg-amber-500/25 hover:border-amber-300/50 transition-colors cursor-pointer"
                  data-testid="admin-panel-stat-30d"
                  aria-label={`View onboarding analytics — ${snapshot.new_30d} new in the last 30 days`}
                >
                  <Flame className="w-3.5 h-3.5 text-amber-300" />
                  <span className="text-xs font-bold text-amber-200 tabular-nums">+{snapshot.new_30d}</span>
                  <span className="text-[10px] uppercase text-amber-300">30d</span>
                </button>
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <Button
            onClick={() => setBroadcastOpen(true)}
            className="bg-fuchsia-600 hover:bg-fuchsia-500 text-white"
            data-testid="admin-panel-hero-broadcast"
          >
            <Megaphone className="w-4 h-4 mr-2" />
            Broadcast
          </Button>
          <Button
            onClick={() => { setAdminViewingAs('reader'); navigate('/dashboard'); }}
            variant="outline"
            className="bg-transparent border-indigo-600 text-indigo-300 hover:bg-indigo-600 hover:text-white"
            data-testid="admin-panel-hero-reader-view"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Reader View
          </Button>
        </div>
      </section>

      {/* Button grid — vertical pills, 1 col on mobile, 2 on md+ */}
      <nav
        className="grid grid-cols-1 sm:grid-cols-2 gap-3"
        data-testid="admin-panel-button-grid"
      >
        {buttons.map((b) => {
          const cls = ACCENT_CLS[b.accent] || ACCENT_CLS.slate;
          return (
            <button
              key={b.key}
              type="button"
              onClick={b.onClick}
              className="group flex items-center gap-3 p-4 rounded-xl bg-slate-800/50 border border-slate-700 hover:bg-slate-800 hover:border-slate-600 transition-colors text-left"
              data-testid={b.testid}
            >
              <span className={`w-10 h-10 rounded-lg ${cls.bg} border ${cls.border} flex items-center justify-center flex-shrink-0`}>
                <b.icon className={`w-5 h-5 ${cls.text}`} />
              </span>
              <span className="flex-1 text-sm sm:text-base font-semibold text-white">{b.label}</span>
              <ChevronRight className="w-4 h-4 text-slate-400 group-hover:text-white transition-colors" />
            </button>
          );
        })}
      </nav>

      <AdminAnnouncementComposer
        open={broadcastOpen}
        onClose={() => setBroadcastOpen(false)}
      />

      {/* God Mode countdown confirm — strict High King gate. Adapts
          its copy + confirm label to whichever of the 4 actions is
          queued in `godAction`. */}
      <ConfirmDeleteDialog
        open={godConfirmOpen && !!godDialog}
        title={godDialog?.title || ''}
        description={godDialog?.description || ''}
        confirmLabel={godDialog?.confirmLabel || 'Confirm'}
        countdownSeconds={5}
        onCancel={() => { setGodConfirmOpen(false); setGodAction(null); }}
        onConfirm={() => {
          if (!godAction) return;
          runGodMode(godAction.action, godAction.scope, godAction.email);
        }}
        testId="admin-panel-godmode-confirm"
      />

      {/* Per-user God Mode targeting modal — High King types an email
          + picks Equip/Strip. Submitting funnels into the same confirm
          dialog above with scope='single'. */}
      {godUserModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
          data-testid="admin-panel-godmode-user-modal"
        >
          <div className="w-full max-w-md bg-[#12131A] border border-white/10 rounded-2xl p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-xl font-black text-white tracking-tight">
                God Mode → Single User
              </h3>
              <button
                onClick={() => setGodUserModalOpen(false)}
                className="text-white/40 hover:text-white text-2xl leading-none"
                aria-label="Close"
                data-testid="admin-panel-godmode-user-modal-close"
              >
                ×
              </button>
            </div>
            <p className="text-xs text-white/50 mb-4 leading-relaxed">
              Type the target user&apos;s email. Equip grants the full perk bundle
              and snapshots their prior state; Strip restores that snapshot
              (or falls back to a safe baseline).
            </p>
            <label className="block text-[10px] uppercase tracking-[0.25em] text-white/40 font-black mb-1.5">
              Email
            </label>
            <input
              type="email"
              value={godUserModalEmail}
              onChange={(e) => setGodUserModalEmail(e.target.value)}
              placeholder="user@example.com"
              autoFocus
              className="w-full bg-[#0B0C10] border border-white/15 rounded-lg px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-amber-400/60"
              data-testid="admin-panel-godmode-user-email"
            />
            <div className="grid grid-cols-2 gap-2 mt-5">
              <button
                onClick={() => {
                  const e = (godUserModalEmail || '').trim();
                  if (!e) { toast.error('Email required'); return; }
                  setGodUserModalOpen(false);
                  setGodAction({ action: 'equip', scope: 'single', email: e.toLowerCase() });
                  setGodConfirmOpen(true);
                }}
                className="px-4 py-2.5 rounded-lg bg-amber-500/15 border border-amber-400/40 text-amber-200 hover:bg-amber-500/25 text-sm font-bold tracking-tight"
                data-testid="admin-panel-godmode-user-equip"
              >
                Equip this user
              </button>
              <button
                onClick={() => {
                  const e = (godUserModalEmail || '').trim();
                  if (!e) { toast.error('Email required'); return; }
                  setGodUserModalOpen(false);
                  setGodAction({ action: 'strip', scope: 'single', email: e.toLowerCase() });
                  setGodConfirmOpen(true);
                }}
                className="px-4 py-2.5 rounded-lg bg-rose-500/15 border border-rose-400/40 text-rose-200 hover:bg-rose-500/25 text-sm font-bold tracking-tight"
                data-testid="admin-panel-godmode-user-strip"
              >
                Strip this user
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default AdminPanel;
